import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LanguageSwitcher } from '../components';
import './Auth.css';

type VerifyStatus = 'verifying' | 'success' | 'error';

export function AuthVerify() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useAuth();

  const [status, setStatus] = useState<VerifyStatus>('verifying');
  const [error, setError] = useState('');
  const [userName, setUserName] = useState('');

  const token = searchParams.get('token');
  const isRTL = i18n.language === 'fa';

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError(isRTL ? 'لینک نامعتبر است' : 'Invalid link');
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch('/api/auth/magic-link/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        const result = await response.json();

        if (result.success && result.data) {
          // Store auth data
          auth.setAuthData({
            user: result.data.user,
            accessToken: result.data.accessToken,
            refreshToken: result.data.refreshToken,
          });

          setUserName(result.data.user.name || result.data.user.email);
          setStatus('success');

          // Redirect to dashboard after 2 seconds
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 2000);
        } else {
          setStatus('error');
          setError(result.error?.message_fa || result.error?.message || 'Verification failed');
        }
      } catch (err) {
        setStatus('error');
        setError(isRTL ? 'خطا در اتصال به سرور' : 'Connection error');
      }
    };

    verifyToken();
  }, [token, navigate, auth, isRTL]);

  return (
    <div className={`auth-page ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="auth-container">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <img src="/logo-header.png" alt="SolarMaktab" />
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="auth-card verify-card">
          {status === 'verifying' && (
            <div className="verify-status">
              <div className="verify-spinner"></div>
              <h2>{isRTL ? 'در حال تأیید...' : 'Verifying...'}</h2>
              <p>{isRTL ? 'لطفاً صبر کنید' : 'Please wait'}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="verify-status success">
              <div className="verify-icon">✓</div>
              <h2>{isRTL ? 'خوش آمدید!' : 'Welcome!'}</h2>
              <p className="verify-name">{userName}</p>
              <p>{isRTL ? 'در حال انتقال به داشبورد...' : 'Redirecting to dashboard...'}</p>
            </div>
          )}

          {status === 'error' && (
            <div className="verify-status error">
              <div className="verify-icon error">✕</div>
              <h2>{isRTL ? 'خطا' : 'Error'}</h2>
              <p className="verify-error">{error}</p>
              <div className="verify-actions">
                <Link to="/login" className="auth-submit-btn">
                  {isRTL ? 'ورود با رمز عبور' : 'Login with password'}
                </Link>
                <Link to="/" className="verify-home-link">
                  {isRTL ? 'بازگشت به صفحه اصلی' : 'Back to home'}
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .verify-card {
          text-align: center;
          padding: 3rem 2rem;
        }
        .verify-status {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }
        .verify-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e5e7eb;
          border-top-color: #2563eb;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .verify-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: bold;
          background: #10b981;
          color: white;
        }
        .verify-icon.error {
          background: #ef4444;
        }
        .verify-status.success h2 {
          color: #10b981;
        }
        .verify-status.error h2 {
          color: #ef4444;
        }
        .verify-name {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1f2937;
        }
        .verify-error {
          color: #6b7280;
        }
        .verify-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 1rem;
          width: 100%;
        }
        .verify-home-link {
          color: #6b7280;
          text-decoration: none;
        }
        .verify-home-link:hover {
          color: #2563eb;
        }
      `}</style>
    </div>
  );
}
